import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Button, ButtonKind, InputLabel, InputMessage, InputMessageKind, TextInput, toast } from '@aposphaere/ui-components'
import logo from '../Icons/ApoLogo.svg'
import { useValidateResetTokenQuery, useResetPasswordMutation } from '../hooks/graphql/password'

type Status = 'loading' | 'valid' | 'invalid'

const validationSchema = Yup.object({
  password: Yup.string().min(8, 'Mindestens 8 Zeichen').max(255, 'Zu lang').required('Pflichtfeld'),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwörter stimmen nicht überein')
    .required('Pflichtfeld'),
})

const ResetPasswordPage: React.FC = () => {
  const location = useLocation()
  const params = new URLSearchParams(location.search)

  const token = params.get('token') || ''
  const email = params.get('email') || ''

  const [status, setStatus] = useState<Status>('loading')

  const { data, isLoading } = useValidateResetTokenQuery({
    email,
    token,
  })
  const { mutateAsync, isLoading: isResetting } = useResetPasswordMutation()

  useEffect(() => {
    if (isLoading) return

    if (data === 'VALID') {
      setStatus('valid')
    } else {
      setStatus('invalid')
    }
  }, [data, isLoading])

  const { handleSubmit, handleChange, values, errors, touched, isSubmitting } = useFormik({
    initialValues: {
      password: '',
      password_confirmation: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const success = await mutateAsync({
          email,
          token,
          password: values.password,
        })

        if (!success) {
          toast.show({
            headline: 'Der Link ist ungültig oder abgelaufen',
            type: 'error',
          })
          return
        }

        toast.show({
          headline: 'Passwort erfolgreich gesetzt',
          type: 'success',
        })

        window.location.href = '/login'
      } catch (e) {
        toast.show({
          headline: 'Fehler beim Zurücksetzen',
          type: 'error',
        })
      }
    },
  })

  return (
    <div className="w-full h-screen flex flex-wrap overflow-hidden bg-orange-100">
      <div className="bg-white h-64 w-110 transform -rotate-3 -mt-32 px-64 -ml-3" />

      <div className="absolute ml-16 mt-16">
        <img className="h-11 w-auto" src={logo} alt="aposphäre CRM" />
      </div>

      <div className="flex absolute w-full h-full justify-center items-center">
        <div className="flex flex-wrap z-50 w-450px bg-white shadow-2xl rounded-lg h-fit justify-center pt-12 pb-12">
          {status === 'loading' && <p>Wird geprüft...</p>}

          {status === 'invalid' && (
            <div className="text-center px-8">
              <h1 className="text-xl text-red-600 mb-4">Link ungültig oder abgelaufen</h1>
              <p className="text-sm text-gray-500">Bitte fordern Sie einen neuen Link an.</p>
            </div>
          )}

          {status === 'valid' && (
            <form onSubmit={handleSubmit} className="w-2/3">
              <h1 className="text-blue-700 text-xl font-medium text-center w-full mb-8">Neues Passwort setzen</h1>

              <div>
                <InputLabel htmlFor="password">Neues Passwort</InputLabel>
                <TextInput type="password" id="password" name="password" value={values.password} onChange={handleChange} />
                {errors.password && touched.password && <InputMessage kind={InputMessageKind.error}>{errors.password}</InputMessage>}
              </div>

              <div className="mt-6">
                <InputLabel htmlFor="password_confirmation">Passwort bestätigen</InputLabel>
                <TextInput
                  type="password"
                  id="password_confirmation"
                  name="password_confirmation"
                  value={values.password_confirmation}
                  onChange={handleChange}
                />
                {errors.password_confirmation && touched.password_confirmation && (
                  <InputMessage kind={InputMessageKind.error}>{errors.password_confirmation}</InputMessage>
                )}
              </div>

              <div className="flex w-full mt-8 justify-center">
                <Button type="submit" kind={ButtonKind.primary} disabled={isSubmitting || isResetting}>
                  {isResetting ? 'Wird gespeichert...' : 'Passwort speichern'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage